import React from 'react';
import { useBarcode } from 'react-barcodes';
import Styles from '../assets/css/productDetail.module.scss';
import productDefultImage from '../assets/images/product/defult.jpeg';
import Loader from '../utils/Loader';

const BarcodeGenerator = React.memo(({ value }) => {
    const { inputRef } = useBarcode({
        value: value || 'NO_BARCODE',
        options: {
            background: '#ffffff',
            width: 2,
            height: 100,
            displayValue: true,
            format: "CODE128"
        }
    });

    return (
        <div className={Styles.barcode_preview}>
            {value && <canvas ref={inputRef} />}
        </div>
    );
});

const ProductDetailCard = ({
    productData,
    productLoading,
    isOutbound,
    maxQuantity,
    selectedSku,
    onSkuChange,
    skuQuantities
}) => {
    const capitalizeFirstLetter = (string) => {
        if (!string) return 'N/A';
        return string.charAt(0).toUpperCase() + string.slice(1);
    };

    // Get available quantity for display
    const getAvailableQuantity = () => {
        if (!productData) return 0;

        // For multiple SKUs, show selected SKU quantity
        if (productData.lazada_product?.data?.skus?.length > 1 && selectedSku) {
            return selectedSku.quantity || 0;
        }

        return productData.originalDbQuantity || productData.quantity || 0;
    };

    // Get total quantity across all SKUs
    const getTotalQuantity = () => {
        if (!productData || !skuQuantities) return 0;
        return Object.values(skuQuantities).reduce((sum, qty) => sum + qty, 0);
    };

    // Get variation display name
    const getVariationDisplay = (variation) => {
        if (!variation) return '';
        return Object.values(variation).join(' - ');
    };

    return (
        <div className={`${Styles.product_card}`}>
            {productLoading ? (
                <div className={`${Styles.product_loading}`}>
                    <Loader size="large" color="black" />
                </div>
            ) : (
                <>
                    {productData?.product_image && productData.product_image.length > 0 ? (
                        <img
                            className={`${Styles.product_image}`}
                            src={productData.product_image[0].image_url || productDefultImage}
                            alt="Product"
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = productDefultImage;
                            }}
                        />
                    ) : (
                        <div className={`${Styles.no_image}`}>No Image Available</div>
                    )}

                    {/* SKU Selector for multiple SKUs */}
                    {productData?.lazada_product?.data?.skus?.length > 1 && (
                        <div className={`${Styles.sku_selector}`}>
                            <label>Select Variant</label>
                            <div className={`${Styles.sku_options}`}>
                                {productData.lazada_product.data.skus.map((sku) => (
                                    <div
                                        key={sku.SkuId}
                                        className={`${Styles.sku_option} ${selectedSku?.SkuId === sku.SkuId ? Styles.selected : ''
                                            }`}
                                        onClick={() => onSkuChange({
                                            SkuId: sku.SkuId,
                                            SellerSku: sku.SellerSku,
                                            quantity: sku.quantity || 0,
                                            variation: sku.saleProp || {}
                                        })}
                                    >
                                        <div className={`${Styles.sku_variant}`}>
                                            {getVariationDisplay(sku.saleProp)}
                                        </div>
                                        <div className={`${Styles.sku_quantity}`}>
                                            Available: {sku.quantity || 0}
                                        </div>
                                        <div className={`${Styles.sku_selected_qty}`}>
                                            Selected: {skuQuantities[sku.SkuId] || 0}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className={`${Styles.product_info}`}>
                        <div className={`${Styles.product_details}`}>
                            <label>Product name</label>
                            <p>{productData?.product_name || 'N/A'}</p>
                        </div>

                        {/* Show selected variant for multiple SKUs */}
                        {productData?.lazada_product?.data?.skus?.length > 1 && selectedSku && (
                            <div className={`${Styles.product_details}`}>
                                <label>Selected Variant</label>
                                <p>{getVariationDisplay(selectedSku.variation)}</p>
                            </div>
                        )}

                        <div className={`${Styles.product_details}`}>
                            <label>Brand name</label>
                            <p>{productData?.brand_name || productData?.lazada_product?.data?.attributes?.brand || 'N/A'}</p>
                        </div>
                        <div className={`${Styles.product_details}`}>
                            <label>Warehouse name</label>
                            <p>{productData?.warehouse?.name || 'N/A'}</p>
                        </div>
                        <div className={`${Styles.product_details}`}>
                            <label>Company name</label>
                            <p>{productData?.company?.company_name || 'N/A'}</p>
                        </div>
                        <div className={`${Styles.product_details}`}>
                            <label>Storage Type</label>
                            <p>{capitalizeFirstLetter(productData?.storage_type) || 'N/A'}</p>
                        </div>
                        <div className={`${Styles.product_details}`}>
                            <label>Expiry date</label>
                            <p>{productData?.expire_date || 'N/A'}</p>
                        </div>
                        <div className={`${Styles.product_details}`}>
                            <label>Barcode</label>
                            {productData?.barcode_number ? (
                                <BarcodeGenerator value={productData.barcode_number} />
                            ) : (
                                <p>No barcode image available</p>
                            )}
                        </div>

                        {/* Show quantity based on SKU type */}
                        {productData?.lazada_product?.data?.skus?.length > 1 ? (
                            <>
                                <div className={`${Styles.product_details}`}>
                                    <label>Selected SKU Quantity</label>
                                    <p>{getAvailableQuantity()}</p>
                                </div>
                                <div className={`${Styles.product_details}`}>
                                    <label>Total All SKUs Quantity</label>
                                    <p>{getTotalQuantity()}</p>
                                </div>
                            </>
                        ) : (
                            <div className={`${Styles.product_details}`}>
                                <label>Available Quantity</label>
                                <p>{getAvailableQuantity()}</p>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default ProductDetailCard;